import { useEffect, useState } from "react";
import { IMAGES } from "../constants/images";
import Sidebar from "../components/Sidebar";
import { FiChevronRight } from "react-icons/fi";
import { database } from "../constants/firebase";
import { ref, onValue } from "firebase/database";
import { ROUTES } from "../App";
import useMoveTo from "../constants/navigation";
import { useUser } from "../contexts/UserContext";
import { ICONS } from "../constants/icons";
import { useParams } from "react-router";
import MobileHeader from "../components/MobileHeader";

const Chapters = () => {
    const navigate = useMoveTo();
    const { currentUser } = useUser();
    const { productId } = useParams();
    const [chapters, setChapters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadedImages, setLoadedImages] = useState({}); // Track image load per chapter
    const [course, setCourse] = useState(null);
    const [hasPurchased, setHasPurchased] = useState(false);
    const [checkingAccess, setCheckingAccess] = useState(true);

    // ✅ Check if user has purchased the course
    useEffect(() => {
        if (!currentUser?.uid || !productId) return;

        const courseRef = ref(database, `Listings/${productId}`);
        const libraryRef = ref(database, `Users/${currentUser.uid}/library/${productId}`);

        // Load course info
        const unsubscribeCourse = onValue(courseRef, (snapshot) => {
            if (snapshot.exists()) {
                setCourse(snapshot.val());
            }
        });

        // Check library entry
        const unsubscribeLibrary = onValue(libraryRef, (snapshot) => {
            const exists = snapshot.exists();
            if (exists) {
                const data = snapshot.val();
                setHasPurchased(!!data.purchasePrice || !!data.totalPaid);
            } else {
                setHasPurchased(false);
            }
            setCheckingAccess(false);
        });

        return () => {
            unsubscribeCourse();
            unsubscribeLibrary();
        };
    }, [currentUser?.uid, productId]);

    // ✅ Check access and redirect if needed
    useEffect(() => {
        if (checkingAccess || !course) return;

        const coursePrice = parseFloat(course.price || 0);
        const isFree = coursePrice === 0;
        const isOwner = course.userId === currentUser?.uid;

        // If course is paid and user hasn't purchased it and isn't the owner, redirect
        if (!isFree && !hasPurchased && !isOwner) {
            window.showToast("Please purchase this course to access its content.", "info");
            navigate(`${ROUTES.PRODUCT_DETAILS}/${productId}`);
        }
    }, [checkingAccess, course, hasPurchased, currentUser?.uid, productId, navigate]);

    useEffect(() => {
        if (!currentUser || !productId) return;

        const chaptersRef = ref(database, `Listings/${productId}/chapters`);
        const unsubscribe = onValue(chaptersRef, (snapshot) => {
            const data = snapshot.val();
            if (data) {
                // Map data to array with id
                const chaptersArray = Object.keys(data).map((key) => ({ id: key, ...data[key] }));
                setChapters(chaptersArray);
            } else {
                setChapters([]);
            }
            setLoading(false);
        });

        return () => unsubscribe();
    }, [currentUser, productId]);

    const handleImageLoad = (id) => {
        setLoadedImages((prev) => ({ ...prev, [id]: true }));
    };

    // Skeleton loader
    const ChapterSkeleton = () => (
        <div className="space-y-4 mt-4">
            {[...Array(4)].map((_, i) => (
                <div key={i} className="flex items-center gap-4 border-t border-gray-200 dark:border-[#D9D9D9]/20 px-4 py-4">
                    <div className="w-16 h-16 bg-gray-300 dark:bg-gray-700 rounded-lg animate-pulse"></div>
                    <div className="flex-1 space-y-2">
                        <div className="h-4 bg-gray-300 dark:bg-gray-700 rounded w-3/5 animate-pulse"></div>
                        <div className="h-3 bg-gray-300 dark:bg-gray-700 rounded w-full animate-pulse"></div>
                        <div className="h-3 bg-gray-300 dark:bg-gray-700 rounded w-4/5 animate-pulse"></div>
                    </div>
                    <div className="w-5 h-5 bg-gray-300 dark:bg-gray-700 rounded animate-pulse"></div>
                </div>
            ))}
        </div>
    );

    return (
        <div className="h-screen bg-white text-black dark:bg-[#1A1A1A] dark:text-white overflow-y-auto">
            <MobileHeader title="Chapters" isBackBtn={true} />

            <div className="flex">
                <Sidebar />
                <main className="flex-1 h-screen pt-5 pb-24 md:pb-20 px-6 overflow-y-auto">
                    {/* Title */}
                    <div className="flex items-center justify-between">
                        <button onClick={() => navigate(-1)} className="flex-shrink-0 md:block hidden">
                            <img src={ICONS.arrowBack} className="w-4 h-4 md:w-5 md:h-5 grayscale brightness-0 dark:invert" alt="Back" />
                        </button>
                        <h2 className="text-2xl md:text-3xl font-semibold hidden md:block">Chapters</h2>
                        <div className="w-5" />
                    </div>

                    {/* Chapter List */}
                    <div className="mt-3 space-y-4 md:mt-16">
                        {loading ? (
                            <ChapterSkeleton />
                        ) : chapters.length === 0 ? (
                            <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                                No Chapters in this Course.
                            </div>
                        ) : (
                            chapters.map((item) => {
                                return (
                                    <div
                                        key={item.id}
                                        className="relative flex items-center justify-between border-t text-black 
                   dark:border-[#D9D9D9]/20 border-gray-200 dark:text-white px-4 py-4 
                   hover:bg-gray-100 dark:hover:bg-[#2A2A2A] transition-colors duration-200 cursor-pointer hover:rounded-lg"
                                        onClick={() => navigate(`${ROUTES.LESSONS_IN_CHAPTER}/${productId}`, { state: { chapterId: item.id } })}
                                    >
                                        <div className="flex items-center gap-4 max-w-[60%]">
                                            {/* Image with shimmer */}
                                            <div className="relative w-16 h-16 flex-shrink-0">
                                                {!loadedImages[item.id] && (
                                                    <div className="absolute inset-0 bg-gray-300 dark:bg-gray-700 rounded-lg animate-pulse"></div>
                                                )}
                                                <img
                                                    src={item.coverImageUrl || IMAGES.courses}
                                                    alt="Course"
                                                    className={`w-16 h-16 rounded-lg object-cover transition-opacity duration-300 ${loadedImages[item.id] ? "opacity-100" : "opacity-0"}`}
                                                    onLoad={() => handleImageLoad(item.id)}
                                                    onError={() => handleImageLoad(item.id)}
                                                />
                                            </div>

                                            <div>
                                                <div className="font-bold text-lg line-clamp-1">{item.title}</div>
                                                <div className="text-sm text-gray-700 dark:text-white line-clamp-3 min-w-full">
                                                    {item.description || "No description provided"}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="text-right min-w-[40px] flex items-center justify-end">
                                            <FiChevronRight className="text-gray-700 dark:text-white text-xl" />
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </main>
            </div>
        </div>
    );
};

export default Chapters;
